import { Cpu, Hash, Network, Server, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CiClusterInfo, CiClusterNode } from "@/lib/types";
import { cn } from "@/lib/utils";

function roleIcon(role: string | undefined) {
  if (role === "server") return Server;
  return Workflow;
}

function NodeTile({ node, logicalLabel }: { node: CiClusterNode; logicalLabel?: string }) {
  const RoleIcon = roleIcon(node.role);
  const address = node.internal_ip || node.ip || "";
  const gpuCount = node.gpu_count ?? 0;
  // A node without any GPU (the Mooncake test's spot CPU boxes) is "cpu".
  const gpuLabel = node.gpu_type ? node.gpu_type.toLowerCase() : gpuCount > 0 ? "gpu" : "cpu";
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2.5 hover:border-primary/40 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <RoleIcon className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="font-mono text-[13px] font-medium text-foreground whitespace-nowrap">
            {node.name}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {logicalLabel && (
            <span className="font-mono text-[10px] text-muted-foreground">{logicalLabel}</span>
          )}
          <span
            className={cn(
              "rounded border px-1 text-[10px] uppercase tracking-wider",
              node.role === "server"
                ? "border-primary/50 text-primary"
                : "border-border text-muted-foreground",
            )}
          >
            {node.role ?? "node"}
          </span>
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {address && (
          <span className="inline-flex items-center gap-1">
            <Network className="size-3" />
            <span className="font-mono">{address}</span>
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Cpu className="size-3" />
          {gpuCount > 0 ? `${gpuCount}× ` : ""}
          {gpuLabel}
        </span>
        {node.nvidia_driver && (
          <span className="whitespace-nowrap">driver {node.nvidia_driver}</span>
        )}
        {node.fleet_machine_id != null && (
          <Link
            to={`/machines/${node.fleet_machine_id}`}
            className="inline-flex items-center gap-0.5 text-primary hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            <Hash className="size-3" />
            {node.fleet_machine_id}
          </Link>
        )}
      </div>
    </div>
  );
}

interface ClusterTopologyCardProps {
  cluster: CiClusterInfo | null | undefined;
}

export function ClusterTopologyCard({ cluster }: ClusterTopologyCardProps) {
  if (!cluster?.nodes?.length) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm font-semibold">
          Cluster topology
          {cluster.provider && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              provider: {cluster.provider}
            </span>
          )}
        </CardTitle>
        <span className="text-xs text-muted-foreground">{cluster.nodes.length} nodes</span>
      </CardHeader>
      <CardContent>
        {/* Flex-wrap so tiles size to their content (full hostname visible)
            rather than getting squeezed into fixed-width grid columns. */}
        <div className="flex flex-wrap gap-2">
          {cluster.nodes.map((node, index) => (
            <NodeTile key={node.name} node={node} logicalLabel={`node${index + 1}`} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
